import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

// max-w-full + overflow-x-auto, because a tab strip is as wide as its labels
// and the phone is not. Settings alone lays out 706px of tabs; on a 390px
// screen that widened the page itself, so the office staff got a zoomed-out
// layout and tabs they could not reach. The strip now scrolls inside its own
// box and the rest of the page stays put.
//
// justify-start rather than justify-center: a centred flex row that overflows
// pushes its first items past the scroll origin, where nothing can scroll back
// to them. With a strip that fits, the two are identical — the list is sized to
// its content either way.
const TabsList = React.forwardRef(({ className, ...props }, ref) => {
  const inner = React.useRef(null);
  React.useImperativeHandle(ref, () => inner.current);

  // Keep the selected tab visible. A strip that scrolls can otherwise leave the
  // active tab off to one side — most often right after a tab is chosen from
  // the far end of it. Only the strip's own scroll position is touched; using
  // scrollIntoView here would also move the page.
  React.useEffect(() => {
    const el = inner.current;
    if (!el) return undefined;
    const reveal = () => {
      const active = el.querySelector('[data-state="active"]');
      if (!active || el.scrollWidth <= el.clientWidth) return;
      const a = active.getBoundingClientRect();
      const box = el.getBoundingClientRect();
      if (a.left < box.left) el.scrollLeft -= (box.left - a.left) + 8;
      else if (a.right > box.right) el.scrollLeft += (a.right - box.right) + 8;
    };
    reveal();
    const observer = new MutationObserver(reveal);
    observer.observe(el, { subtree: true, attributes: true, attributeFilter: ['data-state'] });
    return () => observer.disconnect();
  }, []);

  return (
  <TabsPrimitive.List
    ref={inner}
    className={cn(
      // h-11 on a phone, h-9 from sm up: a 28px trigger inside an h-9 strip is
      // well under the 44px Apple and Google both ask for, and the Communication
      // Hub wraps its four onto two rows 28px apart. Every tab there responded
      // to a scripted tap - a finger just kept missing them.
      "inline-flex h-11 sm:h-9 max-w-full items-center justify-start overflow-x-auto rounded-lg bg-muted p-1 text-muted-foreground",
      "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
      className
    )}
    {...props} />
  );
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md px-3 py-1 min-h-[38px] sm:min-h-0 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
      className
    )}
    {...props} />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props} />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
